import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {useNavigate, useSearchParams} from 'react-router-dom';

import CancellationContext from './CancellationContext';
import CancellationDesktop from './CancellationDesktop';
import CancellationFormContext from './CancellationFormContext';
import CancellationMobile from './CancellationMobile';
import {CANCELLED_TYPE} from './CancellationResult';

import {fetchBookedEvent} from 'Api/Calendar';
import {useDomainLogoFetcher} from 'Components/DomainLogoFetcher';
import {CaptchaError} from 'Error/CaptchaError';
import {ErrorExtEvent404} from 'Error/ErrorMsgConst';
import {HttpError} from 'Error/HttpError';
import {fetchExternalUserInfo} from 'Store/BookAppointmentsStore';
import {
  getCanonicalTz,
} from 'Utils';
import {useEnhancedQuery} from 'Utils/Captcha';
import {EVENT_STATUS, USER_RESPONSE_STATUS} from 'Utils/consts';
import {formatEventInfo} from 'Utils/eventUtils';

export const VIEW_STATE = {
  HOME_VIEW: 'home',
  FORM_VIEW: 'form',
  CONFIRM_VIEW: 'confirm',
};

/**
 * Cancellations Page Component
 * @param {Object} props
 * @return {ReactElement}
 */
export default function Cancellations(props) {
  const {attendeeId, getEventBookerInfo, getRescheduleTo, paramsValid} = props;
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const [isLoaded, setIsLoaded] = useState(false);

  const [isCancelled, setIsCancelled] = useState(false);

  // The current view in the cancellations page flow to display
  const [currView, setCurrView] = useState(searchParams.get('state') || VIEW_STATE.HOME_VIEW);
  const handleBack = () => {
    switch (currView) {
      case VIEW_STATE.FORM_VIEW:
        setCurrView(VIEW_STATE.HOME_VIEW);
        break;
      default:
        break;
    }
  };

  /**
   * {
   *    appointmentId: '',
   *    summary: '',
   *    // email
   *    bookers: [],
   *    startTime: '',
   *    endTime: '',
   *    fromSingleUseLink: '',
   * }
   */
  const [eventInfo, setEventInfo] = useState({});
  const [errorState, setErrorState] = useState({
    isError: false,
    reason: '',
  });
  const [isCancelInProgress, setCancelInProgress] = useState(false);
  const [cancelMessage, setCancelMessage] = useState('');

  // merge derived error state with dynamic error state
  const errorInfo = {
    isError: !paramsValid || errorState.isError,
    reason: (!paramsValid) ? t('error.errorInvalidIds') : t(errorState.reason),
  };

  // handle all errors in the component
  const errorHandler = useCallback((error) => {
    let reason = '';
    console.error(error);
    if (error instanceof CaptchaError) {
      reason = error.message;
    } else if (error instanceof HttpError) {
      switch (error.errorCode) {
        case 400:
          reason = error.body?.error?.message || 'booking.genericHttpValidationError';
          break;
        case 401:
          if (error.message === 'Unauthorized') {
            reason = error.body?.message || 'Unauthorized';
          }
          break;
        case 404:
          reason = ErrorExtEvent404;
          break;
        case 500:
          reason = 'error.errorMsgBody500';
          break;
        default:
          reason = 'error.errorDefault';
          break;
      }
      console.error(error.body);
    } else {
      reason = 'error.errorUnexpected';
    }
    setErrorState({
      isError: true,
      reason: reason,
    });
    setIsLoaded(true);
  }, []);

  const getEventQueryArgs = useMemo(() => ({
    calendarId: attendeeId,
    onError: errorHandler,
    query: async () => {
      const result = await fetchBookedEvent(attendeeId);
      if (result.status === EVENT_STATUS.CANCELLED) {
        setIsCancelled(true);
        setEventInfo(formatEventInfo(result, getEventBookerInfo));
      } else {
        setEventInfo(formatEventInfo(result, getEventBookerInfo));
      }
      setIsLoaded(true);
    },
  }), [attendeeId, errorHandler, getEventBookerInfo]);


  useDomainLogoFetcher(eventInfo?.originalEvent?.user);

  const getBookedEvent = useEnhancedQuery(getEventQueryArgs);
  useEffect(() => {
    if (attendeeId && paramsValid) {
      getBookedEvent();
    }
  }, [attendeeId, paramsValid, getBookedEvent]);

  useEffect(() => {
    // Fetch showPoweredByZoom value from server to determine if we need to show the powered by zoom logo
    if (eventInfo?.originalEvent?.user) {
      dispatch(fetchExternalUserInfo(eventInfo.originalEvent.user));
    }
  }, [dispatch, eventInfo?.originalEvent?.user]);

  let userStatus = '';
  let bookerId = '';
  let timeZone = '';
  const bookerInfo = getEventBookerInfo(eventInfo?.originalEvent);
  if (bookerInfo) {
    userStatus = bookerInfo.responseStatus;
    bookerId = bookerInfo.id;
    if (bookerInfo.timeZone) {
      timeZone = bookerInfo.timeZone;
    } else {
      // If default to event's host timezone:
      //   timeZone = getCanonicalTz(eventInfo?.originalEvent?.start?.timeZone) || 'Etc/UTC';
      timeZone = getCanonicalTz(new Intl.DateTimeFormat().resolvedOptions().timeZone) || 'Etc/UTC';
    }
  }

  let cancelResultType;
  if (isCancelled) {
    cancelResultType = CANCELLED_TYPE.ALREADY_CANCELLED; // Host cancelled
  } else if (currView === VIEW_STATE.CONFIRM_VIEW) {
    cancelResultType = CANCELLED_TYPE.COMPLETED; // Booker just declined
  } else if (userStatus === USER_RESPONSE_STATUS.DECLINED) {
    cancelResultType = CANCELLED_TYPE.ALREADY_DECLINED; // Booker declined
  }

  const onCancel = () => setCurrView(VIEW_STATE.FORM_VIEW);
  const onReschedule = () => {
    navigate(getRescheduleTo(bookerId));
  };

  return (
    <CancellationContext.Provider value={{
      cancelResultType,
      currView,
      setCurrView,
      attendeeId,
      errorInfo,
      eventInfo,
      handleBack,
      isLoaded,
      onCancel,
      onReschedule,
      timeZone,
    }}>
      <CancellationFormContext.Provider value={{
        cancelMessage,
        setCancelMessage,
        isCancelInProgress,
        setCancelInProgress,
        errorState,
        setErrorState,
      }}>
        {isSmallScreen ?
        <CancellationMobile /> :
        <CancellationDesktop />}
      </CancellationFormContext.Provider>
    </CancellationContext.Provider>
  );
}
